import { Directive, ElementRef, inject, signal } from '@angular/core';

/**
 * Manages open/close state for a widget action dropdown.
 * Apply to the anchor element that wraps both the trigger icon and the popup.
 *
 * - Stops `mousedown` and `dblclick` from bubbling to the cdkDragHandle.
 * - Closes the popup when a click lands outside the anchor.
 *
 * Usage:
 *   <div class="filter-anchor" appWidgetDropdown #dd="widgetDropdown">
 *     <button (click)="dd.toggle()">...</button>
 *     @if (dd.isOpen()) { ... <button (click)="dd.close()">X</button> ... }
 *   </div>
 */
@Directive({
    selector: '[appWidgetDropdown]',
    exportAs: 'widgetDropdown',
    host: {
        '(dblclick)': '$event.stopPropagation()',
        '(mousedown)': '$event.stopPropagation()',
        '(document:click)': '_onDocumentClick($event)'
    }
})
export class WidgetDropdownDirective {
    private readonly _el = inject(ElementRef);

    readonly isOpen = signal(false);

    toggle(): void {
        this.isOpen.update(v => !v);
    }

    close(): void {
        this.isOpen.set(false);
    }

    _onDocumentClick(event: MouseEvent): void {
        if (this.isOpen() && !(this._el.nativeElement as HTMLElement).contains(event.target as Node)) {
            this.isOpen.set(false);
        }
    }
}
